import * as Effect from "effect-app/Effect"
import * as S from "effect-app/Schema"
import { type UnionBrand } from "effect-app/Schema"
import type * as Schema from "effect/Schema"
import { type FunctionNode, parse, type SymbolNode } from "mathjs"
import { type HasTimestamps, TimestampProps } from "../Timestamps.ts"
import { isNumeric, splitValues } from "../utils.ts"
import { type ConfiguratorOption, OptionId } from "./Option.ts"
import { OperatorNote, OptionGroupId } from "./shared.ts"

export const ConditionActionType = S.Literals([
  "OPTION_HIDE",
  "OPTION_SHOW",
  "OPTION_SET_VALUE",
  "OPTION_SET_DEFAULT_VALUE",
  "OPTION_ADD_CUSTOMER_NOTE",
  "OPTION_SET_READONLY",
  "OPTION_PRICE_UPDATE",
  "OPTION_SET_PRICE_TABLE",
  "OPTION_SET_PRICE_FORMULA",
  "OPTION_SET_TOOLTIP",
  "OPTION_SET_FOCUSSED_IMAGE_URL",
  "NUMBER_OPTION_SET_MIN_VAL",
  "NUMBER_OPTION_SET_MAX_VAL",
  "NUMBER_OPTION_SET_STEP_SIZE",
  "NUMBER_OPTION_SET_STEP_PRICE",
  "NUMBER_OPTION_SET_STEP_PRICE_DESCRIPTION",
  "LIST_OPTION_ENABLE_ITEM",
  "LIST_OPTION_DISABLE_ITEM",
  "LIST_OPTION_CHANGE_ITEM_IMAGE",
  "SUBTOTAL_SHOW"
])
export type ConditionActionType = typeof ConditionActionType.Type

// Backoffice names of the action kinds, as the condition editor shows them.
// Shared by the label bulk update, the Bedingungen list filter and the
// "Erklären" resolver, so all three speak the same words.
export const conditionActionTitle: Record<ConditionActionType, string> = {
  OPTION_HIDE: "Nicht anzeigen",
  OPTION_SHOW: "Anzeigen",
  OPTION_SET_VALUE: "Eingabewert ändern",
  OPTION_SET_DEFAULT_VALUE: "Standardwert ändern",
  OPTION_ADD_CUSTOMER_NOTE: "Hinweis anzeigen",
  OPTION_SET_READONLY: "Nur lesezugriff",
  OPTION_PRICE_UPDATE: "Preisaufschlag",
  OPTION_SET_PRICE_TABLE: "Preistabelle zuweisen",
  OPTION_SET_PRICE_FORMULA: "Preisformel überschreiben",
  OPTION_SET_TOOLTIP: "Tooltip ändern",
  OPTION_SET_FOCUSSED_IMAGE_URL: "Focus Bild ändern",
  NUMBER_OPTION_SET_MIN_VAL: "Minimalwert überschreiben",
  NUMBER_OPTION_SET_MAX_VAL: "Maximalwert überschreiben",
  NUMBER_OPTION_SET_STEP_SIZE: "Schrittgröße überschreiben",
  NUMBER_OPTION_SET_STEP_PRICE: "Schrittpreis überschreiben",
  NUMBER_OPTION_SET_STEP_PRICE_DESCRIPTION: "Schrittpreis-Beschreibung überschreiben",
  LIST_OPTION_ENABLE_ITEM: "Element aktivieren",
  LIST_OPTION_DISABLE_ITEM: "Element deaktivieren",
  LIST_OPTION_CHANGE_ITEM_IMAGE: "Element-Bild ändern",
  SUBTOTAL_SHOW: "Zwischensumme anzeigen"
}

type ConfiguratorOptionTag = ConfiguratorOption["_tag"]

// How each action's typeValue is shaped. Single source for the editor
// (requireTypeValue, allowedOptionTypes) and the AI patch validator.
export type ConditionActionValueKind =
  | "none" //               no value
  | "free" //               any text / number literal
  | "boolean" //            "true" | "false"
  | "number-or-formula" //  number, or a MathJS formula over option codes
  | "formula" //            a MathJS formula
  | "list-items" //         CSV of item ids of the action's list option
  | "list-items-or-free" // list option: CSV of item ids; other options: literal (number options: evaluated with calc.calculate)
  | "list-item-or-free" //  list option: exactly ONE item id (runtime resolves one id); other options: literal (number options: evaluated with calc.calculate)
  | "price-table" //        a price table id
  | "url" //                an https URL (https/255 is stricter than the runtime, which stores the raw string)
  | "item-images" //        "itemId|url,itemId|url" for the action's list option

export const conditionActionSpec: Record<
  ConditionActionType,
  {
    allowedOptionTypes: readonly ConfiguratorOptionTag[] | null
    value: ConditionActionValueKind
  }
> = {
  OPTION_SHOW: { allowedOptionTypes: null, value: "none" },
  OPTION_HIDE: { allowedOptionTypes: null, value: "none" },
  OPTION_SET_VALUE: { allowedOptionTypes: null, value: "list-items-or-free" },
  OPTION_SET_DEFAULT_VALUE: {
    allowedOptionTypes: null,
    value: "list-item-or-free"
  },
  OPTION_ADD_CUSTOMER_NOTE: { allowedOptionTypes: null, value: "free" },
  // runtime ignores typeValue and always sets readonly (mapOptionConditions.ts), so "false" has no effect
  OPTION_SET_READONLY: { allowedOptionTypes: null, value: "boolean" },
  OPTION_PRICE_UPDATE: { allowedOptionTypes: null, value: "free" },
  OPTION_SET_PRICE_TABLE: { allowedOptionTypes: null, value: "price-table" },
  OPTION_SET_PRICE_FORMULA: { allowedOptionTypes: null, value: "formula" },
  OPTION_SET_TOOLTIP: { allowedOptionTypes: null, value: "free" },
  OPTION_SET_FOCUSSED_IMAGE_URL: { allowedOptionTypes: null, value: "url" },
  NUMBER_OPTION_SET_MIN_VAL: {
    allowedOptionTypes: ["number"],
    value: "number-or-formula"
  },
  NUMBER_OPTION_SET_MAX_VAL: {
    allowedOptionTypes: ["number"],
    value: "number-or-formula"
  },
  NUMBER_OPTION_SET_STEP_SIZE: {
    allowedOptionTypes: ["number"],
    value: "free"
  },
  NUMBER_OPTION_SET_STEP_PRICE: {
    allowedOptionTypes: ["number"],
    value: "free"
  },
  NUMBER_OPTION_SET_STEP_PRICE_DESCRIPTION: {
    allowedOptionTypes: ["number"],
    value: "free"
  },
  LIST_OPTION_ENABLE_ITEM: {
    allowedOptionTypes: ["list"],
    value: "list-items"
  },
  LIST_OPTION_DISABLE_ITEM: {
    allowedOptionTypes: ["list"],
    value: "list-items"
  },
  LIST_OPTION_CHANGE_ITEM_IMAGE: {
    allowedOptionTypes: ["list"],
    value: "item-images"
  },
  SUBTOTAL_SHOW: { allowedOptionTypes: null, value: "none" }
}

// The "no active group" state (the recap step) as a group-rule target.
export const RECAP_GROUP_TARGET = "recap"
// What a recap group rule stores as `groupId` (its comparisonValue is ""): only
// the "this is a group rule" marker, see groupTargetsOfRule.
export const RECAP_GROUP_MARKER = "ACTIVE_GROUP_RULE"

// On a group rule, `groupId` only marks the rule as a group rule; the groups it
// targets live in `comparisonValue` (CSV for in/not_in), and "" means the recap
// state. Same reading as `validateRule` in core/Configuration/mapOptionConditions.ts.
export const groupTargetsOfRule = (
  rule: { readonly groupId: string | null; readonly comparisonValue: string }
): string[] =>
  rule.groupId === null
    ? []
    : rule.comparisonValue === ""
    ? [RECAP_GROUP_TARGET]
    : splitValues(rule.comparisonValue)

/*
 * Extracts variable names from a mathjs formula AST.
 * Filters out function names (like min, ceil) by checking
 * whether the SymbolNode is the function reference of a FunctionNode.
 */
export const getFormulaVariables = (formula: string): string[] => {
  try {
    const ast = parse(formula)
    const vars: string[] = []
    ast.traverse((node, _path, parent) => {
      // as are covered by check on type that TS can't narrow
      if (
        node.type === "SymbolNode" && !(parent?.type === "FunctionNode" && (parent as FunctionNode).fn === node)
      ) {
        vars.push((node as SymbolNode).name)
      }
    })
    return [...new Set(vars)]
  } catch {
    // If the formula cannot be parsed, return an empty list of variables
    return []
  }
}

const validateConditionAction = (input: unknown) => {
  if (typeof input !== "object" || input === null) {
    return undefined
  }

  const a = input as { type: ConditionActionType; typeValue: S.NonEmptyString255 | null }
  if (a.type === "NUMBER_OPTION_SET_MAX_VAL" || a.type === "NUMBER_OPTION_SET_MIN_VAL") {
    if (a.typeValue === null || isNumeric(a.typeValue)) {
      return undefined
    }
    try {
      parse(a.typeValue)
      return undefined
    } catch {
      return "Der Wert muss eine Zahl oder eine gültige Formel für Min-/Max-Aktionen sein"
    }
  }
  return undefined
}
export const conditionActionFilter = S.makeFilter(validateConditionAction)

export const conditionRuleTargetFilter = S.makeFilter((input: unknown) => {
  const { groupId, optionId } = input as { optionId: string | null; groupId: string | null }
  return (optionId === null) === (groupId === null)
    ? "Eine Regel muss genau ein Ziel haben: eine Option ODER eine aktive Gruppe"
    : undefined
})

export class ConditionAction extends S.Opaque<ConditionAction, ConditionAction.Encoded>()(
  S
    .Struct(
      {
        id: S.StringId.withConstructorDefault,
        optionId: OptionId,
        type: ConditionActionType,
        typeValue: S.NullOr(S.NonEmptyString255).withConstructorDefault, // TODO: possibly good to combine into a type with `type` so the value can be constrained?
        ...TimestampProps
      }
    )
    .pipe(S.check(conditionActionFilter))
    .annotate({ identifier: "ConditionAction" })
) implements HasTimestamps {}

export type OpsBase = "==" | "!=" | "<" | "<=" | ">" | ">=" | "in" | "not_in" | "changes"
// works around eslint model plugin issue
export type Ops = (OpsBase) & UnionBrand
export const ComparisonOperator: Schema.Codec<Ops> = S.Literals([
  "==",
  "!=",
  "<",
  "<=",
  ">",
  ">=",
  "in",
  "not_in",
  "changes"
])
export type ComparisonOperator = typeof ComparisonOperator.Type

// Applying rules/formula/calculator
export class ConditionRule extends S.Opaque<ConditionRule, ConditionRule.Encoded>()(
  S
    .Struct({
      id: S.StringId.withConstructorDefault,
      optionId: S.NullOr(OptionId).withConstructorDefault,
      groupId: S.NullOr(OptionGroupId).withConstructorDefault,
      comparisonValue: S.String.pipe(S.check(S.isMaxLength(255))), // Allow empty string for "changes" operator
      comparisonOperator: ComparisonOperator,

      ...TimestampProps
    })
    .pipe(S.check(conditionRuleTargetFilter))
    .annotate({ identifier: "ConditionRule" })
) implements HasTimestamps {}

export const RuleValidationMode = S.Literals(["MATCH_ALL", "MATCH_ANY"])
export type RuleValidationMode = typeof RuleValidationMode.Type

export const ConditionId = S.StringId
export type ConditionId = S.StringId

export class OptionCondition extends S.Opaque<OptionCondition, OptionCondition.Encoded>()(
  S
    .Struct({
      id: ConditionId.withConstructorDefault,
      rules: S.Array(ConditionRule).withConstructorDefault,
      actions: S.Array(ConditionAction).withConstructorDefault,
      ruleValidationMode: RuleValidationMode,
      ...TimestampProps
    })
    .annotate({ identifier: "OptionCondition" })
) implements HasTimestamps {}

export class ConfiguratorConditionGroup
  extends S.Opaque<ConfiguratorConditionGroup, ConfiguratorConditionGroup.Encoded>()(
    S
      .Struct({
        // do we need it?
        id: S.StringId.withConstructorDefault,
        // ConfiguratorConditionGroup
        conditions: S.Array(OptionCondition).withConstructorDefault,
        title: S.NonEmptyString255,
        isEnabled: S.Boolean.pipe(S.withConstructorDefault(Effect.succeed(true))),
        note: OperatorNote,
        ...TimestampProps
      })
      .annotate({ identifier: "ConfiguratorConditionGroup" })
  )
  implements HasTimestamps
{}

// codegen:start {preset: model}
//
export namespace ConditionAction {
  export interface Encoded extends S.StructNestedEncoded<typeof ConditionAction> {}
}
export namespace ConditionRule {
  export interface Encoded extends S.StructNestedEncoded<typeof ConditionRule> {}
}
export namespace OptionCondition {
  export interface Encoded extends S.StructNestedEncoded<typeof OptionCondition> {}
}
export namespace ConfiguratorConditionGroup {
  export interface Encoded extends S.StructNestedEncoded<typeof ConfiguratorConditionGroup> {}
}
//
// codegen:end
